import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/useAuth';
import apiClient from '../api/client';
import { useTheme } from '../useTheme';
import { setTheme, nextTheme } from '../theme';
import ThemeIcon from './ThemeIcon';
import BrandMark from './BrandMark';
import { MoreHorizontal as MoreIcon } from 'lucide-react';
import { markPending } from '../pendingPreferences';
import { chunks } from '../routeChunks';
import { intentHandlers } from '../utils/prefetch';

// A nav link that starts fetching its page chunk on hover or focus. Pointer
// dwell before a click is typically 100-300ms, which covers most of a chunk
// fetch off a warm edge, so the split page is usually resolved by the time
// the click lands and the router never shows its fallback. `onFocus` carries
// the same benefit to keyboard navigation, which fires no hover at all.
const PrefetchLink = ({ chunk, ...props }) => (
  <Link {...props} {...intentHandlers(chunk, chunks[chunk])} />
);

const NavBar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  const theme = useTheme();

  // Both toggles apply the change locally first, then persist. A failed save
  // must not revert what the user just did — but it must not be swallowed
  // either: an empty catch here is exactly what made the system banner's
  // failures invisible for a day.
  const toggleLanguage = async () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
    // Signed out there is no account to write to, so the choice is recorded
    // and promoted at next sign-in rather than being lost.
    if (!isAuthenticated) {
      markPending({ language: newLang });
      return;
    }
    try {
      await apiClient.put('/me/preferences', { language: newLang });
    } catch (err) {
      const status = err?.response?.status;
      console.warn(
        `[NavBar] could not save the language preference${status ? ` (HTTP ${status})` : ''}; it is applied for this session only`,
        err,
      );
    }
  };

  // One button cycling Light -> Dark -> System. An icon cannot show which of
  // three states is active, so the label carries it for both sighted users
  // (title) and screen readers (aria-label).
  const cycleTheme = async () => {
    const next = nextTheme(theme);
    setTheme(next);
    if (!isAuthenticated) {
      markPending({ theme: next });
      return;
    }
    try {
      await apiClient.put('/me/preferences', { theme: next });
    } catch (err) {
      const status = err?.response?.status;
      console.warn(
        `[NavBar] could not save the theme preference${status ? ` (HTTP ${status})` : ''}; it is applied for this session only`,
        err,
      );
    }
  };


  const docsBase = i18n.language === 'fr' ? '/docs/fr' : '/docs';

  return (
    <nav className="navbar">
      <div className="nav-container">
        {/* The brand mark replaces the old "CI Monitoring" wordmark and still
            links home. The square monogram is used rather than the full logo
            because the logo's wordmark is #231f20 and vanishes against the
            dark theme's navbar; no light variant of it exists. */}
        <Link to="/" className="nav-logo">
          <BrandMark variant="dark" alt={t('nav.home')} />
        </Link>

        <div className="nav-menu">
          {isAuthenticated ? (
            <>
              {/* No page link here for the instrument list. It is what `/`
                  renders for everyone but a super_admin, so the logo already
                  goes there -- a second control beside it would be the same
                  destination twice. A super_admin gets there from Accounts. */}

              {/* Settings, help and the legal pages are destinations people
                  visit rarely. Kept inline they crowded out the ones people
                  use constantly; behind one control they stop competing. */}
              <div className="nav-more">
                <button
                  type="button"
                  className="nav-link nav-more-btn"
                  aria-haspopup="menu"
                  aria-label={t('nav.more')}
                  title={t('nav.more')}
                >
                  <MoreIcon />
                </button>
                {/* Opens on hover per the brief, and on focus-within so it is
                    reachable from the keyboard rather than mouse-only. */}
                <div className="nav-more-menu" role="menu">
                  {/* Admin tooling. These were secondary buttons on /admin, so
                      the only route to them was via the logo -- they are
                      top-level destinations and now sit where the other
                      destinations do. Accounts is not among them: it is what
                      `/` renders for a super_admin, so the logo already goes
                      there. Hidden for anyone who is not a super_admin; the
                      routes enforce that independently, since hiding a link is
                      presentation, not access control. */}
                  {isSuperAdmin && (
                    <>
                      <PrefetchLink chunk="adminFleetOverview" to="/admin/fleet" className="nav-more-item" role="menuitem">
                        {t('admin.fleet_overview')}
                      </PrefetchLink>
                      <PrefetchLink chunk="adminReingest" to="/admin/reingest" className="nav-more-item" role="menuitem">
                        {t('admin.reingest')}
                      </PrefetchLink>
                      <PrefetchLink chunk="adminBanner" to="/admin/banner" className="nav-more-item" role="menuitem">
                        {t('admin.banner')}
                      </PrefetchLink>
                      <div className="nav-more-sep" />
                    </>
                  )}
                  <PrefetchLink chunk="settings" to="/settings" className="nav-more-item" role="menuitem">
                    {t('nav.settings')}
                  </PrefetchLink>
                  <PrefetchLink chunk="help" to="/help" className="nav-more-item" role="menuitem">
                    {t('nav.help')}
                  </PrefetchLink>
                  <div className="nav-more-sep" />
                  <a href={`${docsBase}/terms-conditions.html`} target="_blank" rel="noopener noreferrer" className="nav-more-item" role="menuitem">
                    {t('nav.terms')}
                  </a>
                  <a href={`${docsBase}/privacy-policy.html`} target="_blank" rel="noopener noreferrer" className="nav-more-item" role="menuitem">
                    {t('nav.privacy')}
                  </a>
                </div>
              </div>
            </>
          ) : (
            // No sign-in link while standing on the sign-in page. Register
            // still shows one, since from there it is a genuine destination.
            pathname !== '/login' && (
              <PrefetchLink chunk="login" to="/login" className="nav-link">
                {t('nav.login')}
              </PrefetchLink>
            )
          )}

          {/* Appearance and language sit AFTER the page links, and outside the
              auth branch: a user who cannot read the login form in the detected
              language must be able to change it before signing in. Signed-out
              changes are promoted to the account at sign-in. */}
          <button onClick={toggleLanguage} className="nav-link nav-lang-btn" title="Switch language / Changer de langue">
            {t('nav.language')}
          </button>
          <button
            onClick={cycleTheme}
            className="nav-link nav-theme-btn"
            title={t('nav.theme_current', { theme: t(`nav.theme_${theme}`) })}
            aria-label={t('nav.theme_current', { theme: t(`nav.theme_${theme}`) })}
          >
            <ThemeIcon theme={theme} />
          </button>

          {/* The signed-in user's name is deliberately not shown. It told the
              user something they already knew and cost the width the page
              links needed. */}
          {isAuthenticated && (
            <button onClick={logout} className="nav-logout-btn">
              {t('nav.logout')}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
